import axios from 'axios';
import axiosInstance from '../api';
import { SessionManager } from '../../utils/SessionManager';
import { handleErrorAndRedirect } from '../../utils/errorHandler';
import {
  LoginReq,
  LoginRes,
  SignupReq,
  SignupRes,
  UserProfile,
  UpdateProfileReq,
  ChangePasswordReq,
  ChangePasswordRes,
  ForgotPasswordReq,
  ForgotPasswordRes,
  RestoreAccountReq,
  SuccessResponse,
  ProfileUpdateRes,
  IconUpdateRes,
  toUserEntity
} from './authApi.types';

/**
 * ログインAPI
 * @param email ユーザーメールアドレス
 * @param password パスワード
 * @returns ログイン情報（トークンとユーザー情報）
 */
export const loginApi = async (email: string, password: string): Promise<LoginRes> => {
  try {
    const payload: LoginReq = { email, password };
    const response = await axiosInstance.post<LoginRes>('/auth/login', payload);
    return response.data;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

/**
 * 現在のユーザー情報を取得するAPI
 * セッションが有効な場合にユーザー情報を返す
 */
export const getCurrentUser = async (): Promise<UserProfile> => {
  try {
    const response = await axiosInstance.get<UserProfile>('/auth/me');
    return response.data;
  } catch (error) {
    console.error('Error fetching current user:', error);
    throw error;
  }
};

/**
 * サインアップAPI
 * @param email ユーザーメールアドレス
 * @param password パスワード
 * @param name ユーザー名
 */
export const signupApi = async (
  email: string,
  password: string,
  name: string
): Promise<SignupRes> => {
  try {
    const payload: SignupReq = {
      email,
      password,
      name
    };
    const response = await axiosInstance.post<SignupRes>('/auth/signup', payload);
    return response.data;
  } catch (error) {
    console.error('Signup error:', error);
    throw error;
  }
};

/**
 * プロファイル取得API
 * @returns ユーザープロファイル情報
 */
export const getProfile = async (): Promise<UserProfile> => {
  try {
    const response = await axiosInstance.get<UserProfile>('/auth/me');
    return response.data;
  } catch (error) {
    console.error('Error fetching profile:', error);
    throw error;
  }
};

/**
 * プロファイル更新API
 * @param profileData 更新するプロファイルデータ
 * @returns 更新されたプロファイル情報
 */
export const updateProfile = async (profileData: UpdateProfileReq): Promise<ProfileUpdateRes> => {
  try {
    // バックエンド(PUT /api/auth/profile)は { message, user } を返す。
    // response.data を UserProfile として扱うと user フィールド自体を
    // toUserEntity() に渡してしまい id=NaN 等の壊れた値になるため、
    // response.data.user を明示的に取り出す。
    const response = await axiosInstance.put<{ message: string; user: UserProfile }>(
      '/auth/profile',
      profileData,
      { headers: { 'Content-Type': 'application/json' } }
    );

    return { user: response.data.user };
  } catch (error) {
    console.error('Error updating profile:', error);
    throw error;
  }
};

/**
 * パスワード変更API
 * @param oldPassword 現在のパスワード
 * @param newPassword 新しいパスワード
 * @returns 成功レスポンス
 */
export const changePassword = async (
  oldPassword: string,
  newPassword: string
): Promise<ChangePasswordRes> => {
  try {
    const payload: ChangePasswordReq = { oldPassword, newPassword };
    const response = await axiosInstance.put<ChangePasswordRes>(
      '/auth/change-password',
      payload,
      { headers: { 'Content-Type': 'application/json' } }
    );
    // パスワード変更後にバックエンドが新トークンを再発行するため、セッションを更新する
    if (response.data.token) {
      SessionManager.updateToken(response.data.token);
    }
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * アイコン更新API
 * @param formData アイコン画像のFormData
 * @returns 更新されたアイコンを含むプロファイル情報
 */
export const updateIcon = async (formData: FormData): Promise<IconUpdateRes> => {
  try {
    const response = await axiosInstance.put<UserProfile>('/auth/profile/icon', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    
    // 既存コードとの互換性のため、フォーマット変換
    return { iconPath: response.data.icon_path || '' };
  } catch (error) {
    console.error('Error updating icon:', error);
    throw error;
  }
};

/**
 * パスワードリセットリクエストAPI
 * @param email パスワードをリセットするアカウントのメールアドレス
 * @returns パスワードリセットレスポンス（一時パスワード含む）
 */
export const forgotPassword = async (email: string): Promise<ForgotPasswordRes> => {
  try {
    const payload: ForgotPasswordReq = { email };
    const response = await axiosInstance.post<ForgotPasswordRes>('/auth/forgot-password', payload);
    
    // バックエンドから返される実際のレスポンスをそのまま返す
    return response.data;
  } catch (error) {
    console.error('Error in forgot password:', error);
    throw error;
  }
};

/**
 * ログアウトAPI
 * - サーバーにログアウトリクエストを送信
 * - セッションデータは呼び出し元で既に処理済みであることを前提とする
 */
export const logoutApi = async (): Promise<void> => {
  try {
    // ローカルストレージからトークンを削除
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    
    // APIリクエストが失敗してもフロントエンドではログアウト成功とみなす
    try {
      // ログアウト処理の場合はトークンなしで直接呼び出し
      const axiosClient = axios.create({
        baseURL: '/api',
        headers: { 'X-Skip-Auth-Error': 'true' }
      });
      
      await axiosClient.post('/auth/logout');
    } catch (error) {
      console.warn('バックエンドのログアウト処理に失敗しましたが、フロントエンドのセッションは正常にクリアされました。', error);
      // エラーを上位に伝播させない
    }
  } catch (error) {
    console.error('ログアウト処理エラー:', error);
    // どんなエラーが発生しても、ローカルのストレージはクリアしておく
    localStorage.removeItem('token'); 
    sessionStorage.removeItem('token');
  }
};

/**
 * アカウント削除API（論理削除）
 * - ユーザーアカウントを論理削除する
 * - データベースからは削除せず、deleted_atフィールドを設定
 * - 削除後は自動的にログアウト処理も実行
 */
export const deleteAccount = async (): Promise<SuccessResponse> => {
  try {
    const response = await axiosInstance.delete<SuccessResponse>('/auth/account');
    // 削除成功後、ログアウト処理も実行
    await logoutApi();
    return response.data;
  } catch (error) {
    console.error('Account deletion error:', error);
    throw error;
  }
};

/**
 * アカウント復旧API
 * - 論理削除されたアカウントを復旧する
 * - 成功時には自動的にログイン状態になる
 */
export const restoreAccount = async (
  userId: string,
  password: string
): Promise<LoginRes> => {
  try {
    const payload: RestoreAccountReq = { userId, password };
    const response = await axiosInstance.post<LoginRes>('/auth/restore-account', payload);
    
    // トークンを保存（自動ログイン）
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
    }
    
    return response.data;
  } catch (error) {
    console.error('Account restoration error:', error);
    throw error;
  }
}; 